import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, List as F7List, ListItem as F7ListItem, f7 } from 'framework7-react';
import { FC, ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { GagglLogo } from '@assets/GagglLogo';
import { useAppContext } from '@contexts/AppContext';
import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import * as dayjs from 'dayjs';
import { groupBy, isEmpty, keyBy, sortBy } from 'lodash';
import {
  AREAS_OF_IMPROVEMENT_ENABLED,
  RANKINGS_NAVIGATION_ENABLED,
  RATINGS_ENABLED,
  REVIEWS_ENABLED,
} from 'src/constants/FeatureFlags';

import API from '@api/API';

import { CritiqueQuestion } from '@queries/CritiqueQueries';
import {
  Entertainer,
  EntertainerLink,
  EntertainerType,
  TYPES_RECORD,
  UnauthorizedPublicEntertainer,
  useCurrentEntertainerQuery,
  useCurrentUserEntertainerCritiqueQuery,
  useEntertainerByBookingHandleQuery,
  useEntertainerQuickPicksQuery,
  useEntertainerReviewsQuery,
  useRelatedEntertainersByBookingHandleQuery,
  useTopEntertainersQuery,
} from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';

import {
  useAnswerCritiqueQuestionMutation,
  useCritiqueDocumentMutation,
} from '@mutations/CritiqueMutations';

import { useTipEntertainer } from '@hooks/EntertainerHooks/useTipEntertainer';
import { useAnalytics } from '@hooks/useAnalytics';
import { useShareLink } from '@hooks/useShareLink';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { PostCard } from '@components/cards/PostCard/PostCard';
import { Textarea } from '@components/form/Textarea';
import { VideoUploadField } from '@components/form/VideoUploadField';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { PrimaryCritiqueButtons } from '@components/overlays/CritiqueOverlay/CritiqueOverlay';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { QuickPickQuestionOptions } from '@screens/QuickPicksScreen/QuickPicksScreen';
import { EntertainerAvailabilityCard } from '@screens/booking/GigsAndTalentScreen/EntertainerAvailabilityCard';

import { CritiqueList } from '../EntertainerRankingsScreen/EntertainerCritiquePopup';
import { FeaturedLookPopup } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import { EditEntertainerPopup } from './EditEntertainerPopup';
import { EntertainerCompetitionCard } from './EntertainerCompetitionCard';
import { EntertainerMediaCard } from './EntertainerMediaCard';
import { EntertainerReviewsContent } from './EntertainerReviews';
import { sendEntertainerDetailsPageView } from './sendEntertainerDetailsPageView';

const QuickPickCard: FC<{
  documentId: string;
  firstQuestionId: string;
  critiqueQuestion: CritiqueQuestion;
  werqwithme: boolean;
  submitCallback?: () => void;
  initCallback?: () => void;
}> = ({
  documentId,
  firstQuestionId,
  critiqueQuestion,
  werqwithme,
  submitCallback,
  initCallback,
}) => {
  const didAlert = useRef(false);
  const hasAnswered = !!critiqueQuestion?.answer && firstQuestionId === critiqueQuestion._id;
  const [value, setValue] = useState<string>(critiqueQuestion?.answer);
  const { createAuthGateHandler } = useAuthGate();
  const { track } = useAnalytics();
  const { openAlertDialog } = useDialog();

  useLayoutEffect(() => {
    if (hasAnswered && !didAlert.current) {
      didAlert.current = true;
      openAlertDialog({
        title: 'You already answered this question',
        text: 'You can skip to the next question to answer more!',
      });
    } else {
      initCallback?.();
    }
  }, []);

  const { answerCritiqueQuestionAsync } = useAnswerCritiqueQuestionMutation();
  const handleSubmit = () => {
    if (hasAnswered) {
      return submitCallback?.();
    }

    if (value) {
      track('Submit Quick Pick Answer', {
        critiqueQuestionId: critiqueQuestion._id,
        answer: value,
      });

      createAuthGateHandler(async () => {
        await answerCritiqueQuestionAsync({
          critiqueQuestionId: critiqueQuestion._id,
          documentId,
          answer: value,
          meta: { werqwithme },
        });

        submitCallback?.();
      });
    }
  };

  return (
    <Card>
      <Card.Body style={{ borderBottom: '1px solid rgba(255,255,255,.2)' }}>
        <div style={{ fontSize: 16 }}>
          <strong>{critiqueQuestion.question}</strong>
        </div>
      </Card.Body>
      <div className="my-2">
        <QuickPickQuestionOptions
          showSelected
          currentCritiqueQuestion={critiqueQuestion}
          value={value}
          handleSelectOption={setValue}
          onSubmit={handleSubmit}
          submitText={hasAnswered ? 'Next' : 'Submit'}
        />
      </div>
    </Card>
  );
};

export const QuickPicksSection: FC<{
  entertainer: UnauthorizedPublicEntertainer;
  werqwithme: boolean;
  firstQuestionId: string;
}> = ({ entertainer, werqwithme, firstQuestionId }) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { critiqueQuestionsAreReady, critiqueQuestions } = useEntertainerQuickPicksQuery(
    entertainer._id,
    firstQuestionId,
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const titleToolbarRef = useRef<HTMLDivElement>(null);
  const didInitScroll = useRef(false);
  const initCallback = () => {
    if (firstQuestionId && !didInitScroll.current) {
      didInitScroll.current = true;
      setTimeout(() => {
        const targetPosition = titleToolbarRef.current.offsetTop; // Get the top position of the target element relative to the container
        titleToolbarRef.current.closest('.page-content')?.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }, 300);
    }
  };

  const currentCritiqueQuestion = critiqueQuestions?.[currentIndex];
  const hasNextQuestion = critiqueQuestions?.[currentIndex + 1];

  return (
    <>
      <TitleToolbar
        divRef={titleToolbarRef}
        text="Hot takes 🔥"
        size="md"
        action={
          hasNextQuestion && (
            <Button
              variant="flat"
              endIcon={<FontAwesomeIcon name="chevron-right" />}
              onClick={() => setCurrentIndex((prev) => prev + 1)}
            >
              Skip Question
            </Button>
          )
        }
      />
      {critiqueQuestionsAreReady ? (
        <>
          {currentCritiqueQuestion ? (
            <QuickPickCard
              documentId={entertainer._id}
              firstQuestionId={firstQuestionId}
              critiqueQuestion={currentCritiqueQuestion}
              werqwithme={werqwithme}
              submitCallback={() => setCurrentIndex((prev) => prev + 1)}
              initCallback={initCallback}
            />
          ) : (
            <EmptyState
              title="No Questions"
              text="You have already answered all the questions for this entertainer. Go to the live feed to see questions for other entertainers!"
              button={{
                text: 'See Live Feed',
                onClick: () => {
                  track('Click See Live Feed');
                  navigate('/');
                },
              }}
            />
          )}
        </>
      ) : (
        <>
          <Skeleton height={240} />
        </>
      )}
    </>
  );
};

export const EntertainerDetailsTabContent: FC<{
  back?: string;
  preview?: boolean;
  bookingHandle?: string;
  f7route?: {
    query?: {
      firstQuestionId?: string;
      mediaId?: string;
      referrer?: string;
      werqwithme_redirect?: string;
      tab?: string;
      receivedBadgesPopup?: string;
    };
  };
}> = ({ bookingHandle, back: initialBack, preview: isPreviewMode, f7route }) => {
  const { mediaId: initialMediaId, referrer, werqwithme_redirect } = f7route?.query ?? {};
  const werqwithme = werqwithme_redirect === 'true';
  const { currentUser } = useCurrentUser();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const {
    entertainerIsReady,
    entertainer,
    upcomingEntertainerEvents,
    activeEntertainerCompetitions,
  } = useEntertainerByBookingHandleQuery(bookingHandle);
  const { relatedEntertainersAreReady, relatedEntertainers } =
    useRelatedEntertainersByBookingHandleQuery(bookingHandle, entertainerIsReady);
  const { reviews, userPrimaryRating, refetchReviews } = useEntertainerReviewsQuery(
    entertainer?._id,
  );
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const meta = referrer ? { referrer } : undefined;

  const { userIsLoggedIn } = useAppContext();
  const { createAuthGateHandler } = useAuthGate();
  const [primaryRating, setPrimaryRating] = useState<string>();
  const { userEntertainerCritique, userEntertainerCritiqueIsReady } =
    useCurrentUserEntertainerCritiqueQuery(entertainer?._id ?? '', userIsLoggedIn);
  const [isBadgePopupVisible, setIsBadgePopupVisible] = useState(!!initialMediaId);
  const { openAddVideoPopup } = useEntertainerEngagementContext();

  const { openAlertDialog } = useDialog();

  const { isSubmitting: isSubmittingCritique, critiqueDocumentAsync } =
    useCritiqueDocumentMutation();
  const handleUpdatePrimaryRating = (updatedPrimaryRating: string) => {
    track('Click Primary Critique', {
      primaryRating: updatedPrimaryRating,
    });
    createAuthGateHandler(async () => {
      setPrimaryRating(updatedPrimaryRating);
      await critiqueDocumentAsync({
        documentType: 'entertainers',
        documentId: entertainer._id,
        primaryCritique: updatedPrimaryRating,
        meta,
      });
      await refetchReviews();
    });
  };

  const handleSubmitStrength = async (updatedStrengths: string[]) => {
    track('Click Submit Strength', {
      entertainerId: entertainer._id,
      updatedStrengths,
    });
    createAuthGateHandler(async () => {
      await critiqueDocumentAsync({
        documentType: 'entertainers',
        documentId: entertainer._id,
        like: updatedStrengths,
        meta,
      });
    });
  };

  const handleSubmitImprovement = async (updatedImprovements: string[]) => {
    track('Click Submit Improvement', {
      entertainerId: entertainer._id,
      updatedImprovements,
    });
    createAuthGateHandler(async () => {
      await critiqueDocumentAsync({
        documentType: 'entertainers',
        documentId: entertainer._id,
        neutral: updatedImprovements,
        meta,
      });
    });
  };

  const handleEntertainerOnboard = async () => {
    track('Click entertainer onboarding');
    createAuthGateHandler(async () => {
      navigate(`/entertainer/onboarding`, {
        state: {
          back: `/noauth/entertainer/${bookingHandle}`,
        },
      });
    });
  };

  const didShowPreviewAlert = useRef(false);
  useEffect(() => {
    if (isPreviewMode && !didShowPreviewAlert.current) {
      didShowPreviewAlert.current = true;
      openAlertDialog({
        title: 'Preview Mode',
        text: 'This is a preview of your "werqwith.me" link. Share this link with your fans on social media give them an easy way to rate you and get tips and bookings!',
      });
    }
  }, [isPreviewMode]);

  const didRedirectToCurrentEvent = useRef(false);
  useEffect(() => {
    const currentEvent = upcomingEntertainerEvents?.find(({ isHappeningNow }) => isHappeningNow);

    if (currentEvent && !didRedirectToCurrentEvent.current) {
      didRedirectToCurrentEvent.current = true;
      navigate(`/events/${currentEvent._id}`);
    }
  }, [upcomingEntertainerEvents]);

  useEffect(() => {
    if (!isPreviewMode && entertainer && entertainer._id) {
      sendEntertainerDetailsPageView(entertainer._id);
    }
  }, [entertainer]);

  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;

  const badgeShareMedia = useMemo(() => {
    return entertainer?.featuredMedia?.find(({ _id }) => _id === initialMediaId);
  }, [entertainer, initialMediaId]);

  return (
    <>
      {currentUser?.isSuperAdmin && (
        <Button
          color="neutral"
          variant="smooth"
          roundness="rounded"
          onClick={() => {
            openAddVideoPopup({
              cityId: entertainer?.cities?.[0]?._id,
            });
          }}
        >
          Add Featured Video
        </Button>
      )}

      {!!entertainer?.featuredMedia?.length && (
        <>
          <TitleToolbar text="Featured Looks 🧚🏽" size="md" />

          {isCurrentEntertainer &&
            false && ( // it's too much support for other screens/nav
              <div className="mb-2">
                <Button
                  color="neutral"
                  variant="smooth"
                  roundness="rounded"
                  onClick={() => {
                    track('Click View Badges');
                    navigate(`/manage/entertainer/badges?tab=received`, {
                      state: { back: initialBack },
                    });
                  }}
                >
                  View Earned Badges
                </Button>
              </div>
            )}

          {entertainer?.featuredMedia?.length > 1 ? (
            <HorizontalScroll
              items={entertainer?.featuredMedia?.map((media) => (
                <EntertainerMediaCard
                  key={media._id}
                  entertainerId={entertainer._id}
                  media={media}
                  imageHeight={240}
                  meta={{ werqwithme }}
                />
              ))}
            />
          ) : (
            <EntertainerMediaCard
              entertainerId={entertainer._id}
              media={entertainer.featuredMedia?.[0]}
              meta={{ werqwithme }}
            />
          )}
        </>
      )}

      {!userPrimaryRating && !isCurrentEntertainer && (
        <>
          {entertainer?.isPublic && RATINGS_ENABLED && (
            <>
              <div
                className="d-flex flex-column align-items-center mt-4 mb-3"
                style={{
                  border: '1px solid rgba(255, 255, 255, .5)',
                  borderRadius: '1rem',
                  padding: '8px 16px 16px 16px',
                }}
              >
                <TitleToolbar text="Rate This Drag Artist" size="sm" />
                <PrimaryCritiqueButtons
                  value={primaryRating ?? userPrimaryRating}
                  onChange={handleUpdatePrimaryRating}
                />
              </div>
            </>
          )}

          {!currentEntertainer && (
            <div className={entertainer?.featuredMedia?.length ? 'mt-3' : ''}>
              <LinkBanner
                icon={
                  <IconButton className="mx-auto" badge variant="smooth" color="neutral" size="lg">
                    <FontAwesomeIcon name="envelope" />
                  </IconButton>
                }
                title="Are you a drag artist?"
                description="Create your own free landing page for reviews, tips, and bookings!"
                onClick={handleEntertainerOnboard}
              />
            </div>
          )}
        </>
      )}

      {!!activeEntertainerCompetitions?.length && (
        <>
          <TitleToolbar text="Active Competitions 👑" size="md" />
          <HorizontalScroll
            items={activeEntertainerCompetitions.map((activeCompetition) => (
              <EntertainerCompetitionCard
                competition={activeCompetition}
                entertainer={entertainer}
              />
            ))}
          />
        </>
      )}

      {entertainer?.isPublic && (
        <>
          {((userIsLoggedIn && userEntertainerCritiqueIsReady) || !userIsLoggedIn) && (
            <>
              <TitleToolbar text="Known For 👑" size="md" className="mb-0" />
              <div className="mb-2" style={{ fontSize: 12, lineHeight: 1.2, opacity: 0.7 }}>
                <span>
                  {entertainer?.tags?.like?.length
                    ? 'Tap "Yas!" if you agree or add your own!'
                    : 'Be the first to share!'}
                </span>
              </div>
              <CritiqueList
                selectedTags={userEntertainerCritique?.like ?? []}
                tags={entertainer?.tags?.like}
                agreeBtnText="Yas!"
                inputPlaceholder={`What is ${entertainer?.title} known for?`}
                onSubmit={handleSubmitStrength}
              />

              <QuickPicksSection
                entertainer={entertainer}
                werqwithme={werqwithme}
                firstQuestionId={f7route.query.firstQuestionId}
              />

              {AREAS_OF_IMPROVEMENT_ENABLED && (
                <>
                  <TitleToolbar text="Areas for Improvement" size="md" />
                  <div className="mb-2" style={{ fontSize: 12, lineHeight: 1.2, opacity: 0.7 }}>
                    <span>
                      Anonymous community feedback on what this entertainer could improve on.{' '}
                      {entertainer?.tags?.neutral?.length
                        ? 'Tap "Truth!" if you agree or add your own!'
                        : 'Be the first provide constructive feedback!'}
                    </span>
                  </div>
                  {AREAS_OF_IMPROVEMENT_ENABLED ? (
                    <CritiqueList
                      selectedTags={userEntertainerCritique?.neutral ?? []}
                      tags={entertainer?.tags?.neutral}
                      agreeBtnText="Truth!"
                      inputPlaceholder="What could they work on?"
                      onSubmit={handleSubmitImprovement}
                    />
                  ) : (
                    <EmptyState
                      title="Section Locked"
                      text={
                        'Gaggl is committed to being a positive community. After receiving feedback about misuse of this section, it has been locked while we work on ensuring it’s used for constructive feedback. We apologize to those affected by cyberbullying and are taking steps to make Gaggl safer for everyone.'
                      }
                    />
                  )}
                </>
              )}
            </>
          )}

          <TitleToolbar text="Similar Entertainers 👯‍♀️" size="md" />
          {relatedEntertainersAreReady ? (
            <>
              {!!relatedEntertainers?.length ? (
                <HorizontalScroll
                  items={relatedEntertainers.map((relatedEntertainer) => (
                    <EntertainerAvailabilityCard
                      key={relatedEntertainer._id}
                      entertainer={relatedEntertainer}
                      cityId={relatedEntertainer?.cityIds?.[0]}
                      back={`/noauth/entertainer/${bookingHandle}`}
                    />
                  ))}
                />
              ) : (
                <EmptyState
                  title="No Similar Entertainers"
                  text="We don't have any similar entertainers for this entertainer yet"
                />
              )}
            </>
          ) : (
            <Skeleton height={140} />
          )}
        </>
      )}
      {initialMediaId && badgeShareMedia && (
        <FeaturedLookPopup
          isVisible={isBadgePopupVisible}
          onClose={() => {
            setIsBadgePopupVisible(false);
          }}
          entertainer={entertainer}
          entertainerMedia={badgeShareMedia}
        />
      )}
    </>
  );
};
